import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import * as XLSX from 'xlsx';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  DollarSign,
  Calendar,
  Filter,
  Download,
  TrendingUp,
  ArrowRight,
  ArrowLeft,
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const PaymentLedger = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [ledgerData, setLedgerData] = useState({
    daily_summary: [],
    transactions: [],
    total_amount: 0,
    transaction_count: 0,
  });

  // Filters
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [paymentMode, setPaymentMode] = useState('All');
  const [customerFilter, setCustomerFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('All');

  // Customer list
  const [customers, setCustomers] = useState([]);

  const paymentModes = [
    'All',
    'Cash',
    'Card',
    'UPI',
    'IMPS',
    'NEFT/RTGS',
    'Cheque',
    'Old Gold',
    'Metal Credit',
  ];

  const statusOptions = ['All', 'PAYMENT', 'REFUND'];

  useEffect(() => {
    fetchCustomers();
    fetchLedgerData();
  }, []);

  const fetchCustomers = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/customers`);
      if (response.ok) {
        const data = await response.json();
        setCustomers(data);
      }
    } catch (error) {
      console.error('Error fetching customers:', error);
    }
  };

  const fetchLedgerData = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (startDate) params.append('start_date', startDate);
      if (endDate) params.append('end_date', endDate);
      if (paymentMode && paymentMode !== 'All') params.append('payment_mode', paymentMode);

      const response = await fetch(`${BACKEND_URL}/api/reports/payment-ledger?${params.toString()}`);
      if (response.ok) {
        const data = await response.json();
        
        // Apply client-side filters
        let filtered = data.transactions;
        if (customerFilter && customerFilter !== 'all') {
          filtered = filtered.filter(t => t.customer_id === customerFilter);
        }
        if (statusFilter && statusFilter !== 'All') {
          filtered = filtered.filter(t => t.transaction_type === statusFilter);
        }

        setLedgerData({
          ...data,
          transactions: filtered,
          transaction_count: filtered.length,
          total_amount: filtered.reduce((sum, t) => sum + (t.amount || 0), 0),
        });
      }
    } catch (error) {
      console.error('Error fetching payment ledger:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleApplyFilters = () => {
    fetchLedgerData();
  };

  const handleResetFilters = () => {
    setStartDate('');
    setEndDate('');
    setPaymentMode('All');
    setCustomerFilter('all');
    setStatusFilter('All');
  };

  const handleExportToExcel = () => {
    // Prepare data for export
    const exportData = ledgerData.transactions.map(t => ({
      'Date': formatDate(t.date),
      'Payment ID': t.payment_id,
      'Mode': t.mode,
      'Amount': t.amount,
      'Fine Wt (g)': t.fine_wt || '',
      'Source Type': t.source_type,
      'Source ID': t.source_id,
      'Customer ID': t.customer_id,
      'Type': t.transaction_type,
      'Direction': t.direction,
      'Note': t.note || '',
    }));

    // Create workbook
    const wb = XLSX.utils.book_new();
    const ws = XLSX.utils.json_to_sheet(exportData);
    
    // Add worksheet to workbook
    XLSX.utils.book_append_sheet(wb, ws, 'Payment Ledger');
    
    // Generate filename with timestamp
    const filename = `payment_ledger_${new Date().toISOString().split('T')[0]}.xlsx`;
    
    // Download file
    XLSX.writeFile(wb, filename);
  };

  const handleNavigateToSource = (transaction) => {
    const { source_type, source_id } = transaction;
    
    switch (source_type) {
      case 'Order':
        navigate(`/orders/${source_id}`);
        break;
      case 'Booking':
        navigate(`/bookings/${source_id}`);
        break;
      case 'Approval':
        navigate(`/approvals/${source_id}`);
        break;
      case 'Kitty':
        navigate(`/kitty/enrollments`);
        break;
      default:
        break;
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount || 0);
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    });
  };

  const getSourceBadgeColor = (sourceType) => {
    const colors = {
      Order: 'bg-blue-100 text-blue-800',
      Booking: 'bg-purple-100 text-purple-800',
      Approval: 'bg-green-100 text-green-800',
      Kitty: 'bg-orange-100 text-orange-800',
    };
    return colors[sourceType] || 'bg-gray-100 text-gray-800';
  };

  return (
    <div className="container mx-auto p-4 md:p-6 space-y-4 md:space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
        <div className="flex items-center gap-3 w-full sm:w-auto">
          <Button
            variant="outline"
            size="icon"
            onClick={() => navigate(-1)}
            className="flex-shrink-0"
          >
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <div>
            <h1 className="text-2xl md:text-3xl font-bold">Payment Ledger</h1>
            <p className="text-sm text-muted-foreground mt-1">
              Centralized view of all payments across modules
            </p>
          </div>
        </div>
        <Button
          variant="outline"
          onClick={handleExportToExcel}
          className="w-full sm:w-auto"
        >
          <Download className="mr-2 h-4 w-4" />
          Export to Excel
        </Button>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 md:gap-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Amount</CardTitle>
            <DollarSign className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{formatCurrency(ledgerData.total_amount)}</div>
            <p className="text-xs text-muted-foreground">Across all transactions</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Metal Credit</CardTitle>
            <TrendingUp className="h-4 w-4 text-amber-500" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-amber-600">
              {(() => {
                const totalFineWt = ledgerData.transactions
                  .filter(t => t.fine_wt > 0)
                  .reduce((sum, t) => sum + (parseFloat(t.fine_wt) || 0), 0);
                return totalFineWt > 0 ? `${totalFineWt.toFixed(3)} g` : '0 g';
              })()}
            </div>
            <p className="text-xs text-muted-foreground">Total fine weight received</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Transactions</CardTitle>
            <TrendingUp className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{ledgerData.transaction_count}</div>
            <p className="text-xs text-muted-foreground">Payment entries</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Date Range</CardTitle>
            <Calendar className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-sm font-medium">
              {startDate && endDate
                ? `${formatDate(startDate)} - ${formatDate(endDate)}`
                : 'All Time'}
            </div>
            <p className="text-xs text-muted-foreground">Filtered period</p>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base md:text-lg">
            <Filter className="h-5 w-5" />
            Advanced Filters
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3 md:gap-4">
              <div>
                <label className="text-sm font-medium mb-1 block">Start Date</label>
                <Input
                  type="date"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                />
              </div>
              <div>
                <label className="text-sm font-medium mb-1 block">End Date</label>
                <Input
                  type="date"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                />
              </div>
              <div>
                <label className="text-sm font-medium mb-1 block">Payment Mode</label>
                <Select value={paymentMode} onValueChange={setPaymentMode}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select mode" />
                  </SelectTrigger>
                  <SelectContent>
                    {paymentModes.map((mode) => (
                      <SelectItem key={mode} value={mode}>
                        {mode}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 md:gap-4">
              <div>
                <label className="text-sm font-medium mb-1 block">Customer</label>
                <Select value={customerFilter} onValueChange={setCustomerFilter}>
                  <SelectTrigger>
                    <SelectValue placeholder="All Customers" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Customers</SelectItem>
                    {customers.map((customer) => (
                      <SelectItem key={customer.customer_id} value={customer.customer_id}>
                        {customer.name} ({customer.customer_id})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-sm font-medium mb-1 block">Transaction Type</label>
                <Select value={statusFilter} onValueChange={setStatusFilter}>
                  <SelectTrigger>
                    <SelectValue placeholder="All Types" />
                  </SelectTrigger>
                  <SelectContent>
                    {statusOptions.map((status) => (
                      <SelectItem key={status} value={status}>
                        {status}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="flex flex-col sm:flex-row gap-2">
              <Button onClick={handleApplyFilters} className="flex-1">
                Apply Filters
              </Button>
              <Button onClick={handleResetFilters} variant="outline" className="flex-1 sm:flex-none">
                Reset
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Tabs for Daily Summary and Transactions */}
      <Tabs defaultValue="transactions" className="w-full">
        <TabsList>
          <TabsTrigger value="transactions">All Transactions</TabsTrigger>
          <TabsTrigger value="daily">Daily Summary</TabsTrigger>
        </TabsList>

        {/* All Transactions Tab */}
        <TabsContent value="transactions">
          <Card>
            <CardHeader>
              <CardTitle className="text-base md:text-lg">Transaction Details</CardTitle>
              <CardDescription>
                Complete list of all payment transactions
              </CardDescription>
            </CardHeader>
            <CardContent>
              {loading ? (
                <div className="text-center py-8 text-muted-foreground">Loading...</div>
              ) : ledgerData.transactions.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  No transactions found
                </div>
              ) : (
                <>
                  {/* Desktop Table View - Hidden on Mobile */}
                  <div className="hidden lg:block overflow-x-auto">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Date</TableHead>
                          <TableHead>Payment ID</TableHead>
                          <TableHead>Mode</TableHead>
                          <TableHead>Amount</TableHead>
                          <TableHead>Fine Wt (g)</TableHead>
                          <TableHead>Source</TableHead>
                          <TableHead>Source ID</TableHead>
                          <TableHead>Customer ID</TableHead>
                          <TableHead>Type</TableHead>
                          <TableHead>Note</TableHead>
                          <TableHead>Action</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {ledgerData.transactions.map((transaction, idx) => (
                          <TableRow key={`${transaction.payment_id}-${idx}`}>
                            <TableCell>{formatDate(transaction.date)}</TableCell>
                            <TableCell className="font-mono text-xs">
                              {transaction.payment_id}
                            </TableCell>
                            <TableCell>
                              <Badge variant="outline">{transaction.mode}</Badge>
                            </TableCell>
                            <TableCell className="font-semibold">
                              {formatCurrency(transaction.amount)}
                            </TableCell>
                            <TableCell className="font-semibold">
                              {transaction.fine_wt ? `${parseFloat(transaction.fine_wt).toFixed(3)} g` : '-'}
                            </TableCell>
                            <TableCell>
                              <Badge className={getSourceBadgeColor(transaction.source_type)}>
                                {transaction.source_type}
                              </Badge>
                            </TableCell>
                            <TableCell className="font-mono text-xs">
                              {transaction.source_id}
                            </TableCell>
                            <TableCell className="font-mono text-xs">
                              {transaction.customer_id}
                            </TableCell>
                            <TableCell>
                              <Badge variant={transaction.transaction_type === 'REFUND' ? 'destructive' : 'default'}>
                                {transaction.transaction_type}
                              </Badge>
                            </TableCell>
                            <TableCell className="text-xs text-muted-foreground">
                              {transaction.note || '-'}
                            </TableCell>
                            <TableCell>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleNavigateToSource(transaction)}
                              >
                                <ArrowRight className="h-4 w-4" />
                              </Button>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>

                  {/* Mobile Card View */}
                  <div className="lg:hidden space-y-3">
                    {ledgerData.transactions.map((transaction, idx) => (
                      <Card key={`${transaction.payment_id}-${idx}`} className="overflow-hidden">
                        <CardContent className="p-4">
                          <div className="space-y-3">
                            {/* Header Row */}
                            <div className="flex items-start justify-between">
                              <div>
                                <Badge variant="outline" className="text-xs">
                                  {transaction.mode}
                                </Badge>
                                <div className="text-lg font-bold mt-1">
                                  {transaction.mode === 'Metal Credit' && transaction.fine_wt
                                    ? `${parseFloat(transaction.fine_wt).toFixed(3)} g`
                                    : formatCurrency(transaction.amount)}
                                </div>
                                {transaction.mode === 'Metal Credit' && transaction.fine_wt > 0 && (
                                  <div className="text-xs text-muted-foreground">Metal Credit</div>
                                )}
                              </div>
                              <Badge className={`${getSourceBadgeColor(transaction.source_type)} text-xs`}>
                                {transaction.source_type}
                              </Badge>
                            </div>

                            {/* Details Grid */}
                            <div className="grid grid-cols-2 gap-2 text-xs">
                              <div>
                                <span className="text-muted-foreground">Date:</span>{' '}
                                <span className="font-medium">{formatDate(transaction.date)}</span>
                              </div>
                              <div>
                                <span className="text-muted-foreground">Type:</span>{' '}
                                <Badge variant={transaction.transaction_type === 'REFUND' ? 'destructive' : 'default'} className="text-xs">
                                  {transaction.transaction_type}
                                </Badge>
                              </div>
                              <div className="col-span-2">
                                <span className="text-muted-foreground">Source ID:</span>{' '}
                                <span className="font-mono text-xs">{transaction.source_id}</span>
                              </div>
                              <div className="col-span-2">
                                <span className="text-muted-foreground">Payment ID:</span>{' '}
                                <span className="font-mono text-xs">{transaction.payment_id}</span>
                              </div>
                              <div className="col-span-2">
                                <span className="text-muted-foreground">Customer ID:</span>{' '}
                                <span className="font-mono text-xs">{transaction.customer_id}</span>
                              </div>
                              {transaction.note && (
                                <div className="col-span-2">
                                  <span className="text-muted-foreground">Note:</span>{' '}
                                  <span>{transaction.note}</span>
                                </div>
                              )}
                            </div>

                            {/* Action Button */}
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleNavigateToSource(transaction)}
                              className="w-full"
                            >
                              View Source
                              <ArrowRight className="ml-2 h-3 w-3" />
                            </Button>
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                </>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Daily Summary Tab */}
        <TabsContent value="daily">
          <Card>
            <CardHeader>
              <CardTitle className="text-base md:text-lg">Daily Summary</CardTitle>
              <CardDescription>Payment totals grouped by day</CardDescription>
            </CardHeader>
            <CardContent>
              {loading ? (
                <div className="text-center py-8 text-muted-foreground">Loading...</div>
              ) : ledgerData.daily_summary.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  No summary available
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Date</TableHead>
                        <TableHead>Total Amount</TableHead>
                        <TableHead>Transaction Count</TableHead>
                        <TableHead>Breakdown by Mode</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {ledgerData.daily_summary.map((summary) => (
                        <TableRow key={summary.date}>
                          <TableCell className="font-medium">
                            {formatDate(summary.date)}
                          </TableCell>
                          <TableCell className="font-bold text-lg">
                            {formatCurrency(summary.total_amount)}
                          </TableCell>
                          <TableCell>
                            <Badge variant="secondary">{summary.transaction_count}</Badge>
                          </TableCell>
                          <TableCell>
                            <div className="flex flex-wrap gap-2">
                              {Object.entries(summary.by_mode).map(([mode, amount]) => (
                                <Badge key={mode} variant="outline" className="text-xs">
                                  {mode}: {formatCurrency(amount)}
                                </Badge>
                              ))}
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default PaymentLedger;
